import {
  testingTabObject,
  auditTestingTabObject,
  SOC2testingTabObject
} from '../constants/testingConfigs';

/**
 * Strategy Pattern implementation for Testing Sheet configurations
 * Determines which testing configuration to use based on type
 */
class TestingSheetStrategy {
  /**
   * Get the appropriate configuration object based on template type
   * @param {string} type - The template type
   * @param {Object} dynamicTestingTabObject - Dynamic configuration for custom types
   * @returns {Object} - The appropriate testing configuration object
   */
  static getConfigForType(type, dynamicTestingTabObject = null) {
    // BDO Audit types
    if (type === 'BDO_SAP' || type === 'BDO_GENERIC') {
      return auditTestingTabObject;
    }

    // SOC types
    if (type === 'SOC2.0' || type === 'SOC1.0' || type === 'ISAE 3402') {
      return SOC2testingTabObject;
    }

    // MAZAR types (MAZAR, Template M, Template B1/B2)
    // AUMYAA types (AUMYAA-SAP, AUMYAA-GENERIC, Template A1/A2)
    // SAIL types (SAIL, Template S1)
    // RBI types (RBI, Template R)
    // All use the same testingTabObject configuration
    if (
      type === 'MAZAR' ||
      type === 'Template M' ||
      type === 'Template B1' ||
      type === 'Template B2' ||
      type === 'AUMYAA-SAP' ||
      type === 'AUMYAA-GENERIC' ||
      type === 'Template A1' ||
      type === 'Template A2' ||
      type === 'SAIL' ||
      type === 'Template S1' ||
      type === 'RBI' ||
      type === 'Template R'
    ) {
      return testingTabObject;
    }

    // Custom/Dynamic configuration
    if (dynamicTestingTabObject) {
      return dynamicTestingTabObject;
    }

    // Default fallback
    return testingTabObject;
  }

  /**
   * Determine if individual Master Table sheets should be rendered
   * @param {string} type - The template type
   * @returns {boolean} - True if individual sheets should be rendered
   *
   * NOTE: MAZAR and Template M are intentionally NOT included in this list.
   * MAZAR templates use only tab-based sheets and do NOT create individual
   * testing sheets for each Master Table row.
   */
  static shouldRenderMasterTableSheets(type) {
    const masterTableTypes = [
      'BDO_SAP',
      'Template B1',
      'BDO_GENERIC',
      'Template B2',
      'SOC2.0',
      'SOC1.0',
      'ISAE 3402',
      'BDO-D365',
      'BDO-SAP',
      'BDO-Generic',
      'BDO-Oracle',
      'BDO-Tally'
    ];

    return masterTableTypes.includes(type);
  }

  /**
   * Get the field name for overall conclusion based on type
   * @param {string} type - The template type
   * @returns {string} - The field name for overall conclusion
   */
  static getOverallConclusionField(type) {
    // SOC types and MAZAR use 'Overall Conclusion (Effective / Ineffective)'
    if (
      type === 'SOC2.0' ||
      type === 'SOC1.0' ||
      type === 'ISAE 3402' ||
      type === 'MAZAR' ||
      type === 'Template M'
    ) {
      return 'Overall Conclusion (Effective / Ineffective)';
    }

    // AUMYAA and SAIL use 'Conclusion (Effective / Ineffective)'
    if (
      type === 'AUMYAA-SAP' ||
      type === 'AUMYAA-GENERIC' ||
      type === 'Template A1' ||
      type === 'Template A2' ||
      type === 'SAIL' ||
      type === 'Template S1'
    ) {
      return 'Conclusion (Effective / Ineffective)';
    }

    // BDO and Template B types use 'Result'
    return 'Result';
  }

  /**
   * Determine tab color style based on overall conclusion
   * @param {string} overallConclusion - The overall conclusion value
   * @returns {Object} - Style object for the tab
   */
  static getTabColorStyle(overallConclusion) {
    if (overallConclusion) {
      return { color: 'green', fontWeight: '800' };
    }
    return { color: 'red' };
  }
}

export default TestingSheetStrategy;
